const BASE_PATH = "/market/animaux";

export const ANIMALS_TABS = ["adoption", "garde", "entraide", "perdus"];

const TAB_CONFIG = {
  adoption: {
    defaultActive: "publier",
    defaultLegacy: "publier",
    canonical: ["publier", "parcourir", "favori", "compat", "rencontre"],
    legacy: ["publier", "parcourir", "favori", "compat", "rencontre"],
    canonicalToLegacy: {},
    legacyToCanonical: {},
  },
  garde: {
    defaultActive: "proposer",
    defaultLegacy: "proposer",
    canonical: ["proposer", "demander", "calendrier", "confiance"],
    legacy: ["publier", "proposer", "demander", "parcourir", "favori", "mesgardes", "gestion"],
    canonicalToLegacy: {
      proposer: "proposer",
      demander: "demander",
      calendrier: "mesgardes",
      confiance: "gestion",
    },
    legacyToCanonical: {
      publier: "proposer",
      proposer: "proposer",
      demander: "demander",
      mesgardes: "calendrier",
      gestion: "confiance",
    },
  },
  entraide: {
    defaultActive: "urgence",
    defaultLegacy: "publier",
    canonical: ["urgence", "nourriture", "transport", "conseils"],
    legacy: ["publier", "parcourir", "mesentraides"],
    canonicalToLegacy: {
      urgence: "parcourir",
      nourriture: "parcourir",
      transport: "parcourir",
      conseils: "parcourir",
    },
    legacyToCanonical: {
      publier: "urgence",
      parcourir: "urgence",
      mesentraides: "urgence",
    },
  },
  perdus: {
    defaultActive: "signaler",
    defaultLegacy: "publier",
    canonical: ["signaler", "recherche", "alerte", "retrouver"],
    legacy: ["publier", "parcourir", "messignalements", "detail"],
    canonicalToLegacy: {
      signaler: "publier",
      recherche: "parcourir",
      alerte: "parcourir",
      retrouver: "messignalements",
    },
    legacyToCanonical: {
      publier: "signaler",
      parcourir: "recherche",
      messignalements: "retrouver",
      detail: "recherche",
    },
  },
};

const LEGACY_PARAM_BY_TAB = {
  garde: "garde",
  entraide: "entraide",
  perdus: "perdus",
};

function clean(value) {
  return String(value || "").trim().toLowerCase();
}

function toSearchParams(input) {
  if (input instanceof URLSearchParams) return new URLSearchParams(input);
  if (input && typeof input === "object") {
    return toSearchParams(input.search || "");
  }

  const raw = String(input || "").trim();
  if (!raw) return new URLSearchParams();
  if (/^https?:\/\//i.test(raw)) {
    try {
      return new URL(raw).searchParams;
    } catch (_error) {
      return new URLSearchParams();
    }
  }
  const queryIndex = raw.indexOf("?");
  return new URLSearchParams((queryIndex >= 0 ? raw.slice(queryIndex + 1) : raw).replace(/^\?/, ""));
}

function inferredTab(params) {
  const explicit = clean(params.get("tab"));
  if (ANIMALS_TABS.includes(explicit)) return explicit;
  if (params.has("garde")) return "garde";
  if (params.has("entraide")) return "entraide";
  if (params.has("perdus")) return "perdus";
  return "adoption";
}

function canonicalDetail(config, value) {
  const candidate = clean(value);
  if (config.canonical.includes(candidate)) return candidate;
  if (config.legacy.includes(candidate)) {
    return config.legacyToCanonical[candidate] || config.defaultActive;
  }
  return config.defaultActive;
}

function mappedLegacyDetail(config, activeDetail) {
  return config.canonicalToLegacy[activeDetail] ||
    (config.legacy.includes(activeDetail) ? activeDetail : config.defaultLegacy);
}

/**
 * Read the canonical `tab`/`detail` pair while keeping compatibility with the
 * historical per-tab parameters consumed by MarketAnimauxPage.
 */
export function parseAnimalsRouteState(input = "", options = {}) {
  const params = toSearchParams(input);
  const routePostId = String(
    options.routePostId || options.postId || (input && typeof input === "object" ? input.routePostId || input.postId : "") || ""
  ).trim();

  if (routePostId) {
    return {
      tab: "adoption",
      activeDetail: "parcourir",
      legacyDetail: "parcourir",
      legacyParam: null,
      routePostId,
      isPostRoute: true,
    };
  }

  const tab = inferredTab(params);
  const config = TAB_CONFIG[tab];
  const legacyParam = LEGACY_PARAM_BY_TAB[tab] || null;
  const rawDetail = clean(params.get("detail"));
  const explicitLegacy = legacyParam ? clean(params.get(legacyParam)) : "";
  const validExplicitLegacy = config.legacy.includes(explicitLegacy) ? explicitLegacy : "";
  const activeSource = rawDetail || validExplicitLegacy;
  const activeDetail = canonicalDetail(config, activeSource);
  const legacyDetail = validExplicitLegacy || mappedLegacyDetail(config, activeDetail);

  return {
    tab,
    activeDetail,
    legacyDetail,
    legacyParam,
    routePostId: "",
    isPostRoute: false,
  };
}

/**
 * Build a shareable canonical URL. Unrelated filters are retained, obsolete
 * legacy-tab parameters are removed, and the relevant historical parameter is
 * emitted alongside `tab`/`detail` for the existing views.
 */
export function serializeAnimalsRouteState(state = {}, options = {}) {
  const pathname = String(options.pathname || state.pathname || BASE_PATH).split("?")[0] || BASE_PATH;
  const currentSearch = options.currentSearch ?? state.currentSearch ?? "";
  const params = toSearchParams(currentSearch);
  const requestedTab = clean(state.tab);
  const tab = ANIMALS_TABS.includes(requestedTab) ? requestedTab : "adoption";
  const config = TAB_CONFIG[tab];
  const requestedDetail = state.activeDetail ?? state.detail;
  const activeDetail = canonicalDetail(config, requestedDetail);
  const requestedLegacy = clean(state.legacyDetail);
  const legacyDetail = config.legacy.includes(requestedLegacy)
    ? requestedLegacy
    : mappedLegacyDetail(config, activeDetail);

  params.set("tab", tab);
  params.set("detail", activeDetail);
  Object.values(LEGACY_PARAM_BY_TAB).forEach((name) => params.delete(name));

  const legacyParam = LEGACY_PARAM_BY_TAB[tab];
  if (legacyParam) params.set(legacyParam, legacyDetail);

  const postId = String(state.postId || "").trim();
  if (postId) params.set("post", postId);

  const query = params.toString();
  return `${pathname}${query ? `?${query}` : ""}`;
}

export const buildAnimalsRouteUrl = serializeAnimalsRouteState;

